import {logger} from "../../logger/logger";
import {LogCategory} from "../../../logging/types";

/** Größere Bilder werden verkleinert; als JPEG bleibt ein Screenshot so meist unter 1 MB. */
const MAX_WIDTH = 1920;
const JPEG_QUALITY = 0.75;

/**
 * Nimmt die aktuelle Seite als JPEG-Data-URL auf. html2canvas zeichnet das DOM
 * nach: iframes und manches CSS fehlen im Bild. Die Bibliothek wird erst hier
 * geladen, das Haupt-Bundle bleibt gleich groß.
 * Liefert `undefined`, wenn die Aufnahme scheitert; die Meldung geht dann ohne Bild.
 */
export const capturePage = async (): Promise<string | undefined> => {
    try {
        const {default: html2canvas} = await import(/* webpackChunkName: "html2canvas" */ "html2canvas");
        const canvas = await html2canvas(document.body, {
            logging: false,
            scale: Math.min(1, MAX_WIDTH / window.innerWidth),
            width: window.innerWidth,
            height: window.innerHeight,
            x: window.scrollX,
            y: window.scrollY,
            // Das gerade geschlossene Hilfe-Menü gehört nicht ins Bild
            ignoreElements: (element) => element.classList.contains("ant-dropdown"),
        });

        return canvas.toDataURL("image/jpeg", JPEG_QUALITY);
    } catch (error) {
        logger.warn("Screenshot für Fehlermeldung fehlgeschlagen", {category: LogCategory.SYSTEM, error});

        return undefined;
    }
};

/** Eigenes Bild (eingefügt oder hochgeladen) als verkleinertes JPEG. */
export const imageFileToJpeg = async (file: Blob): Promise<string> => {
    const url = URL.createObjectURL(file);

    try {
        const image = new Image();

        await new Promise<void>((resolve, reject) => {
            image.onload = () => resolve();
            image.onerror = () => reject(new Error("Bild konnte nicht gelesen werden"));
            image.src = url;
        });

        const scale = Math.min(1, MAX_WIDTH / image.naturalWidth);
        const canvas = document.createElement("canvas");

        canvas.width = Math.round(image.naturalWidth * scale);
        canvas.height = Math.round(image.naturalHeight * scale);
        canvas.getContext("2d")?.drawImage(image, 0, 0, canvas.width, canvas.height);

        return canvas.toDataURL("image/jpeg", JPEG_QUALITY);
    } finally {
        URL.revokeObjectURL(url);
    }
};
